'use client';

import { useCallback, useEffect, useRef } from 'react';

import {
  isTrustedPremierePluginOrigin,
  type PremiereToAirActiveSequence,
  type PremiereToAirChaptersCreated,
  PremiereToAirEvent,
  type PremiereToAirExportComplete,
  type PremiereToAirExportPresets,
  type PremiereToAirExportProgress,
  type PremiereToAirExportQueued,
  type PremiereToAirHostInfo,
  type PremiereToAirImportBatchComplete,
  type PremiereToAirImportComplete,
  type PremiereToAirImportProgress,
  type PremiereToAirMarkersCreated,
  type PremiereToAirThemeChanged,
  type PremiereToAirTranscriptImported,
  type PremiereToAirUploadComplete,
  type PremiereToAirUploadProgress,
} from '@air/premiere-constants';

import { isPremiereToAirMessage } from '~/lib/pluginMessages';

export type PremiereMessageHandlers = {
  onActiveSequence?: (data: PremiereToAirActiveSequence['data']) => void;
  onTranscriptImported?: (data: PremiereToAirTranscriptImported['data']) => void;
  onChaptersCreated?: (data: PremiereToAirChaptersCreated['data']) => void;
  onExportComplete?: (data: PremiereToAirExportComplete['data']) => void;
  onExportPresets?: (data: PremiereToAirExportPresets['data']) => void;
  onExportProgress?: (data: PremiereToAirExportProgress['data']) => void;
  onExportQueued?: (data: PremiereToAirExportQueued['data']) => void;
  onHostInfo?: (data: PremiereToAirHostInfo['data']) => void;
  onImportBatchComplete?: (data: PremiereToAirImportBatchComplete['data']) => void;
  onImportComplete?: (data: PremiereToAirImportComplete['data']) => void;
  onImportProgress?: (data: PremiereToAirImportProgress['data']) => void;
  onMarkersCreated?: (data: PremiereToAirMarkersCreated['data']) => void;
  onReady?: () => void;
  onThemeChanged?: (data: PremiereToAirThemeChanged['data']) => void;
  onUploadComplete?: (data: PremiereToAirUploadComplete['data']) => void;
  onUploadProgress?: (data: PremiereToAirUploadProgress['data']) => void;
};

/**
 * Listens for PremiereToAirEvent messages from the UXP host and dispatches to callbacks.
 * Attach only the handlers you need — unset handlers are silently ignored.
 */
export const usePremiereMessaging = (handlers: PremiereMessageHandlers) => {
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  const handleMessage = useCallback((event: MessageEvent) => {
    // UXP uses manifest id as origin (host has no web URL); same-origin for dev; empty for legacy builds.
    if (
      event.origin !== '' &&
      event.origin !== window.location.origin &&
      !isTrustedPremierePluginOrigin(event.origin)
    ) {
      return;
    }

    const { data: message } = event;

    if (!isPremiereToAirMessage(message)) {
      return;
    }

    const h = handlersRef.current;

    switch (message.type) {
      case PremiereToAirEvent.Ready:
        h.onReady?.();
        break;
      case PremiereToAirEvent.HostInfo:
        h.onHostInfo?.(message.data);
        break;
      case PremiereToAirEvent.ImportBatchComplete:
        h.onImportBatchComplete?.(message.data);
        break;
      case PremiereToAirEvent.ImportComplete:
        h.onImportComplete?.(message.data);
        break;
      case PremiereToAirEvent.ImportProgress:
        h.onImportProgress?.(message.data);
        break;
      case PremiereToAirEvent.MarkersCreated:
        h.onMarkersCreated?.(message.data);
        break;
      case PremiereToAirEvent.ChaptersCreated:
        h.onChaptersCreated?.(message.data);
        break;
      case PremiereToAirEvent.ExportQueued:
        h.onExportQueued?.(message.data);
        break;
      case PremiereToAirEvent.ExportProgress:
        h.onExportProgress?.(message.data);
        break;
      case PremiereToAirEvent.ExportComplete:
        h.onExportComplete?.(message.data);
        break;
      case PremiereToAirEvent.ExportPresets:
        h.onExportPresets?.(message.data);
        break;
      case PremiereToAirEvent.ActiveSequence:
        h.onActiveSequence?.(message.data);
        break;
      case PremiereToAirEvent.UploadProgress:
        h.onUploadProgress?.(message.data);
        break;
      case PremiereToAirEvent.UploadComplete:
        h.onUploadComplete?.(message.data);
        break;
      case PremiereToAirEvent.TranscriptImported:
        h.onTranscriptImported?.(message.data);
        break;
      case PremiereToAirEvent.ThemeChanged:
        h.onThemeChanged?.(message.data);
        break;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);
};
